import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Webhook, Plus, Trash2, PlayCircle, ArrowLeft, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { LogoFull } from '@/components/Logo';

const AVAILABLE_EVENTS = [
  { value: 'policy.created', label: 'Policy Created' },
  { value: 'policy.updated', label: 'Policy Updated' },
  { value: 'payment.completed', label: 'Payment Completed' },
];

const WebhookManager = () => {
  const { user } = useAuth();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState(['policy.created', 'policy.updated', 'payment.completed']);
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    try {
      const data = await api.getWebhooks();
      setWebhooks(data);
    } catch (err) {
      console.error('Failed to load webhooks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!newUrl.trim()) return;

    try {
      const webhook = await api.registerWebhook(newUrl, selectedEvents);
      setWebhooks(prev => [...prev, webhook]);
      setNewUrl('');
      setShowForm(false);
      toast.success('Webhook registered successfully');
    } catch {
      toast.error('Failed to register webhook');
    }
  };

  const handleDelete = async (webhookId) => {
    try {
      await api.deleteWebhook(webhookId);
      setWebhooks(prev => prev.filter(w => w.webhook_id !== webhookId));
      toast.success('Webhook deleted');
    } catch {
      toast.error('Failed to delete webhook');
    }
  };

  const handleTest = async (webhookId) => {
    setTestResults(prev => ({ ...prev, [webhookId]: 'loading' }));
    try {
      const result = await api.testWebhook(webhookId);
      setTestResults(prev => ({ ...prev, [webhookId]: result.status }));
      if (result.status === 'sent') {
        toast.success(`Test sent. Response: ${result.response_code}`);
      } else {
        toast.error(`Test failed: ${result.error}`);
      }
    } catch {
      setTestResults(prev => ({ ...prev, [webhookId]: 'failed' }));
      toast.error('Test request failed');
    }
  };

  const toggleEvent = (event) => {
    setSelectedEvents(prev =>
      prev.includes(event) ? prev.filter(e => e !== event) : [...prev, event]
    );
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="webhook-manager-page">
      {/* Header */}
      <header className="bg-white border-b border-[#DCE4ED]">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <Link to="/admin" className="flex items-center gap-2 text-[#8DA0B9] hover:text-[#0D1B2A] transition-colors">
              <ArrowLeft size={18} />
              <span className="text-sm">Admin Dashboard</span>
            </Link>
            <div className="flex items-center gap-2">
              <LogoFull size="small" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Webhook size={24} className="text-[#1A3A64]" />
              <h1 className="text-2xl font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Webhooks
              </h1>
              <span className="px-2 py-0.5 bg-[#1A3A64]/10 text-[#1A3A64] text-xs font-medium rounded-full">
                {webhooks.length} registered
              </span>
            </div>
            <Button onClick={() => setShowForm(!showForm)} className="btn-primary" data-testid="add-webhook-btn">
              <Plus size={16} className="mr-2" />
              Add Webhook
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Register Form */}
        <AnimatePresence>
          {showForm && (
            <motion.form
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              onSubmit={handleRegister}
              className="bg-white rounded-xl border border-[#DCE4ED] p-6 mb-6 overflow-hidden"
            >
              <h3 className="font-medium text-[#0D1B2A] mb-4">Register New Webhook</h3>
              <div className="mb-4">
                <label className="text-sm text-[#3D5A80] mb-1.5 block">Endpoint URL</label>
                <Input
                  value={newUrl}
                  onChange={e => setNewUrl(e.target.value)}
                  placeholder="https://your-domain.com/webhook"
                  className="border-[#DCE4ED]"
                  data-testid="webhook-url-input"
                />
              </div>
              <div className="mb-4">
                <label className="text-sm text-[#3D5A80] mb-2 block">Events</label>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_EVENTS.map(ev => (
                    <button
                      key={ev.value}
                      type="button"
                      onClick={() => toggleEvent(ev.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        selectedEvents.includes(ev.value)
                          ? 'bg-[#1A3A64] text-white border-[#1A3A64]'
                          : 'bg-white text-[#3D5A80] border-[#DCE4ED]'
                      }`}
                      data-testid={`event-toggle-${ev.value}`}
                    >
                      {ev.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="btn-primary" data-testid="register-webhook-btn">Register</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Webhooks List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[#1A3A64] border-t-transparent rounded-full mx-auto" />
          </div>
        ) : webhooks.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#DCE4ED]">
            <Webhook size={40} className="text-[#DCE4ED] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#0D1B2A] mb-2">No Webhooks Registered</h3>
            <p className="text-[#8DA0B9]">Add a webhook to receive real-time notifications.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {webhooks.map(wh => (
              <motion.div
                key={wh.webhook_id}
                layout
                className="bg-white rounded-xl border border-[#DCE4ED] p-5"
                data-testid={`webhook-${wh.webhook_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <code className="text-sm text-[#0D1B2A] font-mono break-all">{wh.url}</code>
                    <p className="text-xs text-[#8DA0B9] mt-1">ID: {wh.webhook_id}</p>
                  </div>
                  <div className="flex items-center gap-1 ml-3">
                    <span className={`w-2 h-2 rounded-full ${wh.is_active ? 'bg-emerald-500' : 'bg-red-400'}`} />
                    <span className="text-xs text-[#8DA0B9]">{wh.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {wh.events?.map(ev => (
                    <span key={ev} className="px-2 py-0.5 bg-[#F0F4F8] text-[#3D5A80] rounded text-xs font-mono">
                      {ev}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-[#DCE4ED]">
                  <div className="flex items-center gap-3 text-xs text-[#8DA0B9]">
                    {wh.failure_count > 0 && (
                      <span className="flex items-center gap-1 text-amber-600">
                        <AlertCircle size={12} /> {wh.failure_count} failures
                      </span>
                    )}
                    {wh.last_triggered && (
                      <span>Last: {new Date(wh.last_triggered).toLocaleDateString()}</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTest(wh.webhook_id)}
                      disabled={testResults[wh.webhook_id] === 'loading'}
                      data-testid={`test-webhook-${wh.webhook_id}`}
                    >
                      {testResults[wh.webhook_id] === 'loading' ? (
                        <span className="animate-spin w-4 h-4 border-2 border-[#1A3A64] border-t-transparent rounded-full" />
                      ) : testResults[wh.webhook_id] === 'sent' ? (
                        <CheckCircle size={14} className="text-emerald-500" />
                      ) : testResults[wh.webhook_id] === 'failed' ? (
                        <XCircle size={14} className="text-red-500" />
                      ) : (
                        <PlayCircle size={14} />
                      )}
                      <span className="ml-1.5">Test</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(wh.webhook_id)}
                      className="text-[#E74C3C] hover:text-[#E74C3C] hover:bg-[#E74C3C]/5"
                      data-testid={`delete-webhook-${wh.webhook_id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebhookManager;
